import { forwardRef, memo, useEffect, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { isEmpty, noop } from 'lodash';
// Import UI components
import { ExtendedInput } from '@geneui/components';
// Import constants
import { l } from 'constants/common';

interface Props {
    value?: string;
    placeholder?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange: (value: any) => void;
}

interface SimpleInputFormRef {
    submitForm: () => Promise<boolean>;
    reset: () => void;
}

const SimpleInputForm = forwardRef<SimpleInputFormRef, Props>(({ value, placeholder, onChange }, ref) => {
    const { t } = useTranslation();
    const formik = useFormik({
        initialValues: {
            simpleField: '',
        },
        onSubmit: noop,
        validationSchema: Yup.object({
            simpleField: Yup.string().required(t(l.CannotBeEmpty, { fieldName: t(l.Name) })),
        }),
    });
    const { errors, touched, setFieldValue, handleBlur, submitForm, validateForm, setFieldTouched, values } = formik;

    useImperativeHandle(ref, () => ({
        submitForm() {
            return new Promise((resolve) => {
                submitForm().then(() =>
                    validateForm(values).then((formErrors) => {
                        setFieldTouched('simpleField');
                        resolve(isEmpty(formErrors));
                        return;
                    }),
                );
            });
        },
        reset() {
            setFieldTouched('simpleField', false);
        },
    }));

    const onChangeValue = (e) => {
        setFieldValue('simpleField', e?.value);
        onChange(e);
    };

    useEffect(() => {
        setFieldValue('simpleField', value);
    }, [setFieldValue, value]);

    const cleanUp = () => () => {
        onChange(null);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(cleanUp, []);

    return (
        <ExtendedInput
            type="text"
            name="simpleField"
            onBlur={handleBlur}
            isValid={touched.simpleField ? !errors.simpleField : true}
            errorText={touched.simpleField && errors.simpleField}
            onChange={onChangeValue}
            value={value}
            placeholder={placeholder}
            autocomplete="off"
        />
    );
});

SimpleInputForm.displayName = 'SimpleInputForm';

export default memo(SimpleInputForm);
